import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useTheme } from '@/contexts/ThemeContext';
import { useProject } from '@/contexts/ProjectContext';
import { PermissionGate } from '@/components/PermissionGate';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Package, Search, RefreshCw, Plus, FileSpreadsheet, Filter, Sparkles, ClipboardCheck,
  LayoutList, Grid3X3, Loader2, ChevronLeft, X, List, Truck, Radio, Clock, BookUser,
} from 'lucide-react';

// Sub-components
import MaterialTypesConfig from '@/components/procurement/MaterialTypesConfig';
import MCIStageGateConfig from '@/components/procurement/MCIStageGateConfig';
import MCIRegisterList from '@/components/procurement/MCIRegisterList';
import MCIMatrixGrid from '@/components/procurement/MCIMatrixGrid';
import MCIExcelImportExport from '@/components/procurement/MCIExcelImportExport';
import GuidedImportWizard from '@/components/shared/GuidedImportWizard';
import UniversalFilterBar from '@/components/procurement/UniversalFilterBar';
import { PROCUREMENT_MCI_CONFIG } from '@/components/shared/importFieldConfigs';
import MCIAnalytics from '@/components/procurement/MCIAnalytics';
import MCILongLeadTracker from '@/components/procurement/MCILongLeadTracker';
import ProcurementAnalytics from '@/components/procurement/ProcurementAnalytics';
import ControlTowerPreOrder from '@/components/procurement/ControlTowerPreOrder';
import ControlTowerPostOrder from '@/components/procurement/ControlTowerPostOrder';
import DeliveryForecast from '@/components/procurement/DeliveryForecast';
import VendorContactBook from '@/components/procurement/VendorContactBook';
import POPerformance from '@/components/procurement/POPerformance';
import POLineItemPicker from '@/components/procurement/POLineItemPicker';
import BulkForecastReview from '@/components/procurement/BulkForecastReview';
import ModuleHelp from '@/components/common/ModuleHelp';
import ModuleInsightsDashboard from '@/components/common/ModuleInsightsDashboard';
import CognaraMacroLinkModal from '@/components/construction/CognaraMacroLinkModal';

const TABS = [
  { id: 'proc-analytics', label: 'Procurement Analytics', icon: Grid3X3 },
  { id: 'register',  label: 'MCI Register',        icon: LayoutList },
  { id: 'ct-pre',    label: 'Control Tower · Pre', icon: Radio },
  { id: 'ct-post',   label: 'Control Tower · Post', icon: Truck },
  { id: 'delivery',  label: 'Delivery Forecast',   icon: Clock },
  { id: 'vendors',   label: 'Vendor Book',         icon: BookUser },
  { id: 'po',        label: 'PO Performance',      icon: ClipboardCheck },
  { id: 'analytics', label: 'S-Curve & Analytics', icon: Grid3X3 },
  { id: 'longlead',  label: 'Long Lead Tracker',   icon: Filter },
  { id: 'config',    label: 'Stage Gate Config',   icon: Grid3X3 },
  { id: 'types',     label: 'Material Types',      icon: Package },
];

const CATEGORY_FILTERS = ['All', 'BULK', 'TAGGED'];
const PHASE_FILTERS = ['All', 'PRE_ORDER', 'POST_ORDER', 'COMPLETED'];

function AddEntryDialog({ projectId, onClose, onCreated }) {
  const [configs, setConfigs] = useState([]);
  const [form, setForm] = useState({
    materialCategory: 'TAGGED', materialTypeCode: '',
    tagNumber: '', bulkItemCode: '',
    materialDescription: '', discipline: '', area: '', areaCode: '',
    quantity: 1, unit: 'NOS',
    mrNumber: '', mrDate: '',
    requiredAtSiteDate: '', plannedLeadTimeDays: 90,
    priority: 'NORMAL', isLongLead: false, isCriticalPath: false,
    wbsCode: '', linkedActivityId: '',
    defaultReceiptLocation: '',
    poNumber: '', poDate: '', poValue: '', poCurrency: 'USD', poLineItem: '',
    vendor: '', vendorContactName: '', vendorContactEmail: '', vendorContactPhone: '',
    poDocumentRef: '', vendorDrawingRef: '',
    commodityGroup: '', remarks: '',
  });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basic');

  useEffect(() => {
    api.get(`/v1/projects/${projectId}/mci/stage-gate-configs`).then(r => setConfigs(r.data)).catch(() => {});
  }, [projectId]);

  const handleSave = async () => {
    if (!form.materialTypeCode || !form.materialDescription) {
      toast.error('Material type and description required'); return;
    }
    setSaving(true);
    try {
      const cfg = configs.find(c => c.materialTypeCode === form.materialTypeCode);
      const payload = { ...form,
        quantity: parseFloat(form.quantity) || 0,
        plannedLeadTimeDays: parseInt(form.plannedLeadTimeDays) || 0,
        poValue: form.poValue ? parseFloat(form.poValue) : null,
        materialTypeName: cfg?.materialTypeName || form.materialTypeCode,
        stageGateConfigId: cfg?.id };
      await api.post(`/v1/projects/${projectId}/mci/entries`, payload);
      toast.success('Entry created');
      onCreated(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create entry'); }
    setSaving(false);
  };
  const typeOptions = configs.filter(c => c.materialCategory === form.materialCategory);
  const inputCls = "w-full px-2 py-1.5 rounded-md text-sm border";
  const inputSty = { background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' };
  const labelCls = "text-[10px] uppercase font-semibold block mb-1";
  const labelSty = { color: 'var(--text-muted)' };
  const TABS_F = [
    { id: 'basic',   label: 'Basic Info' },
    { id: 'req',     label: 'Requirement & WBS' },
    { id: 'po',      label: 'PO & Vendor' },
    { id: 'docs',    label: 'Documents & Notes' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="add-entry-dialog">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-xl p-5 shadow-2xl space-y-3"
        style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Add MCI Entry</h3>
            <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Rich intake — every field feeds the Control Towers, Delivery Forecast and Cognara insights.</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>

        {/* Section tabs */}
        <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border-color)' }}>
          {TABS_F.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`entry-tab-${t.id}`}
              className="px-3 py-1.5 text-[11px] font-semibold"
              style={{ color: tab === t.id ? '#1E4E8C' : 'var(--text-muted)',
                        borderBottom: tab === t.id ? '2px solid #1E4E8C' : '2px solid transparent' }}>{t.label}</button>
          ))}
        </div>

        {tab === 'basic' && (
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls} style={labelSty}>Category *</label>
              <select value={form.materialCategory} onChange={e => setForm({ ...form, materialCategory: e.target.value, materialTypeCode: '' })} className={inputCls} style={inputSty} data-testid="entry-category">
                <option value="BULK">BULK · streamlined 4/6 gates</option>
                <option value="TAGGED">TAGGED · full 7/10 gates</option>
              </select>
              <div className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }}>BULK skips CBE, PO Recommend/Approve, Engineering Docs, FAT.</div>
            </div>
            <div><label className={labelCls} style={labelSty}>Material Type *</label>
              <select value={form.materialTypeCode} onChange={e => setForm({ ...form, materialTypeCode: e.target.value })} className={inputCls} style={inputSty} data-testid="entry-type">
                <option value="">Select…</option>
                {typeOptions.map(c => <option key={c.materialTypeCode} value={c.materialTypeCode}>{c.materialTypeCode} — {c.materialTypeName}</option>)}
              </select>
            </div>
            {form.materialCategory === 'TAGGED' ? (
              <div><label className={labelCls} style={labelSty}>Tag Number</label>
                <input value={form.tagNumber} onChange={e => setForm({ ...form, tagNumber: e.target.value })} placeholder="P-101A" className={inputCls} style={inputSty} /></div>
            ) : (
              <div><label className={labelCls} style={labelSty}>Bulk Item Code</label>
                <input value={form.bulkItemCode} onChange={e => setForm({ ...form, bulkItemCode: e.target.value })} placeholder="CS-PIPE-6IN-SCH40" className={inputCls} style={inputSty} /></div>
            )}
            <div><label className={labelCls} style={labelSty}>Commodity Group</label>
              <input value={form.commodityGroup} onChange={e => setForm({ ...form, commodityGroup: e.target.value })} placeholder="e.g. Piping" className={inputCls} style={inputSty} /></div>
            <div className="col-span-2"><label className={labelCls} style={labelSty}>Description *</label>
              <input value={form.materialDescription} onChange={e => setForm({ ...form, materialDescription: e.target.value })} className={inputCls} style={inputSty} data-testid="entry-desc" /></div>
            <div><label className={labelCls} style={labelSty}>Discipline</label>
              <select value={form.discipline} onChange={e => setForm({ ...form, discipline: e.target.value })} className={inputCls} style={inputSty}>
                <option value="">Select…</option>
                {['Mechanical', 'Piping', 'Structural', 'Civil', 'Electrical', 'Instrumentation', 'Metallurgical'].map(d => <option key={d}>{d}</option>)}
              </select></div>
            <div><label className={labelCls} style={labelSty}>Area</label>
              <select value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} className={inputCls} style={inputSty}>
                <option value="">Select…</option>
                {['Unit-100 CDU', 'Unit-200 VDU', 'Unit-300 HDT', 'Unit-400 FCC', 'Unit-500 Utilities', 'Tank Farm', 'Flare Area', 'Interconnecting'].map(a => <option key={a}>{a}</option>)}
              </select></div>
            <div><label className={labelCls} style={labelSty}>Quantity</label>
              <input type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>Unit</label>
              <select value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} className={inputCls} style={inputSty}>
                {['NOS', 'm', 'kg', 'ton', 'm²', 'm³', 'set', 'lot'].map(u => <option key={u}>{u}</option>)}
              </select></div>
            <div><label className={labelCls} style={labelSty}>Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className={inputCls} style={inputSty} data-testid="entry-priority">
                {['CRITICAL', 'HIGH', 'NORMAL', 'LOW'].map(p => <option key={p}>{p}</option>)}
              </select></div>
            <div className="flex items-center gap-4 pt-4">
              <label className="text-[11px] flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                <input type="checkbox" checked={form.isLongLead} onChange={e => setForm({ ...form, isLongLead: e.target.checked })} /> Long-Lead
              </label>
              <label className="text-[11px] flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                <input type="checkbox" checked={form.isCriticalPath} onChange={e => setForm({ ...form, isCriticalPath: e.target.checked })} /> Critical Path
              </label>
            </div>
          </div>
        )}

        {tab === 'req' && (
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls} style={labelSty}>MR Number</label>
              <input value={form.mrNumber} onChange={e => setForm({ ...form, mrNumber: e.target.value })} placeholder="MR-ADR-XXXXX" className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>MR Date</label>
              <input type="date" value={form.mrDate} onChange={e => setForm({ ...form, mrDate: e.target.value })} className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>Required at Site (RAS)</label>
              <input type="date" value={form.requiredAtSiteDate} onChange={e => setForm({ ...form, requiredAtSiteDate: e.target.value })} className={inputCls} style={inputSty} data-testid="entry-ras" />
              <div className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Drives Delivery Forecast buffer.</div></div>
            <div><label className={labelCls} style={labelSty}>Planned Lead-Time (days)</label>
              <input type="number" value={form.plannedLeadTimeDays} onChange={e => setForm({ ...form, plannedLeadTimeDays: e.target.value })} className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>WBS Code</label>
              <input value={form.wbsCode} onChange={e => setForm({ ...form, wbsCode: e.target.value })} placeholder="1.2.3.4" className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>Linked Activity ID (WBS)</label>
              <input value={form.linkedActivityId} onChange={e => setForm({ ...form, linkedActivityId: e.target.value })} placeholder="ACT-1234" className={inputCls} style={inputSty} />
              <div className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Links RAS to construction schedule.</div></div>
            <div className="col-span-2"><label className={labelCls} style={labelSty}>Delivery / GRN / IQC Location</label>
              <input value={form.defaultReceiptLocation} onChange={e => setForm({ ...form, defaultReceiptLocation: e.target.value })}
                placeholder="e.g. WH-01 · Piping Yard · Bay 3"
                className={inputCls} style={inputSty} data-testid="entry-default-location" />
              <div className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Auto-prefills every receipt and IQC bin decision on this MCI. All 3 events happen at the same warehouse.</div></div>
          </div>
        )}

        {tab === 'po' && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className={labelCls} style={labelSty}>PO + Line Item (search existing)</label>
              <POLineItemPicker projectId={projectId}
                value={form.poNumber ? { poNumber: form.poNumber, poLineItem: form.poLineItem } : null}
                onChange={v => {
                  if (!v) { setForm({ ...form, poNumber: '', poLineItem: '', vendor: form.vendor, poValue: form.poValue }); return; }
                  setForm({ ...form, poNumber: v.poNumber, poLineItem: v.poLineItem || '',
                    vendor: v.vendor || form.vendor,
                    poValue: v.poValue || form.poValue });
                }} />
            </div>
            <div><label className={labelCls} style={labelSty}>PO Number</label>
              <input value={form.poNumber} onChange={e => setForm({ ...form, poNumber: e.target.value })} placeholder="PO-ADR-XXXXX" className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>PO Line Item</label>
              <input value={form.poLineItem} onChange={e => setForm({ ...form, poLineItem: e.target.value })} placeholder="L01" className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>PO Date</label>
              <input type="date" value={form.poDate} onChange={e => setForm({ ...form, poDate: e.target.value })} className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>PO Value</label>
              <input type="number" value={form.poValue} onChange={e => setForm({ ...form, poValue: e.target.value })} className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>Currency</label>
              <select value={form.poCurrency} onChange={e => setForm({ ...form, poCurrency: e.target.value })} className={inputCls} style={inputSty}>
                {['USD', 'EUR', 'INR', 'AED', 'GBP', 'JPY'].map(c => <option key={c}>{c}</option>)}
              </select></div>
            <div><label className={labelCls} style={labelSty}>Vendor</label>
              <input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} placeholder="Vendor name" className={inputCls} style={inputSty} data-testid="entry-vendor" /></div>
            <div><label className={labelCls} style={labelSty}>Vendor Contact Name</label>
              <input value={form.vendorContactName} onChange={e => setForm({ ...form, vendorContactName: e.target.value })} className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>Vendor Contact Email</label>
              <input type="email" value={form.vendorContactEmail} onChange={e => setForm({ ...form, vendorContactEmail: e.target.value })} className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>Vendor Contact Phone</label>
              <input value={form.vendorContactPhone} onChange={e => setForm({ ...form, vendorContactPhone: e.target.value })} className={inputCls} style={inputSty} /></div>
          </div>
        )}

        {tab === 'docs' && (
          <div className="grid grid-cols-1 gap-3">
            <div><label className={labelCls} style={labelSty}>PO Document Ref (URL / DMS)</label>
              <input value={form.poDocumentRef} onChange={e => setForm({ ...form, poDocumentRef: e.target.value })} placeholder="doc://po/PO-XXX.pdf" className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>Vendor Drawing Ref (VDR)</label>
              <input value={form.vendorDrawingRef} onChange={e => setForm({ ...form, vendorDrawingRef: e.target.value })} placeholder="doc://vdr/VDR-XXX.pdf" className={inputCls} style={inputSty} /></div>
            <div><label className={labelCls} style={labelSty}>Remarks / Notes</label>
              <textarea rows={4} value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} placeholder="Free-form notes for Cognara context" className={inputCls} style={inputSty} /></div>
          </div>
        )}

        <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {form.materialCategory === 'BULK' ? '4-gate pre / 6-gate post workflow' : '7-gate pre / 10-gate post workflow'}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-entry-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Create Entry
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProcurementSchedule() {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('register');  const [entries, setEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [phaseFilter, setPhaseFilter] = useState('All');
  const [showExcel, setShowExcel] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showAutoLink, setShowAutoLink] = useState(false);
  const [viewMode, setViewMode] = useState('register');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const { selectedProject, projectId: ctxProjectId } = useProject();
  const activeProjectId = selectedProject?.project_id;

  const runRegisterCognara = async () => {
    if (!activeProjectId) return;
    setBulkBusy(true);
    try {
      const params = new URLSearchParams();
      if (catFilter !== 'All') params.append('materialCategory', catFilter);
      const { data: r } = await api.post(
        `/v1/projects/${activeProjectId}/mci/control-tower/gate-forecast/register-cognara-suggest?${params}`);
      if (!r.count) { toast.info('Nothing to forecast — no open gates match the filter.'); setBulkBusy(false); return; }
      setBulkRows(r.rows || []);
      setBulkOpen(true);
    } catch (e) { toast.error('Cognara register-wide suggest failed'); }
    setBulkBusy(false);
  };

  // Fetch entries
  const fetchEntries = useCallback(async () => {
    if (!activeProjectId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (catFilter !== 'All') params.append('category', catFilter);
      if (phaseFilter !== 'All') params.append('phase', phaseFilter);
      if (search) params.append('search', search);
      params.append('limit', '500');
      const { data } = await api.get(`/v1/projects/${activeProjectId}/mci/entries?${params}`);
      setEntries(data.entries || []);
      setTotalEntries(data.total || 0);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [activeProjectId, catFilter, phaseFilter, search]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Deep-link from Procurement Analytics drilldowns → switch tab in-place
  useEffect(() => {
    const handler = (e) => {
      const t = e?.detail?.tab;
      if (t) setActiveTab(t);
    };
    window.addEventListener('procurement.setTab', handler);
    return () => window.removeEventListener('procurement.setTab', handler);
  }, []);

  return (
    <DashboardLayout>
      {ctxProjectId && <AIAnalystPanel projectId={ctxProjectId} moduleKey="PROCUREMENT_SCHEDULE" defaultOpen={true} compact={true} />}

      <div className="space-y-4 p-4 md:p-6" data-testid="procurement-schedule-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Procurement & Supply Chain Schedule</h1>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Material Control Index (MCI) — Track materials from MR to site receipt</p>
          </div>
          <div className="flex items-center gap-2">
            <ModuleHelp moduleKey="procurement" />
            <Button size="sm" variant="outline" onClick={() => setShowAutoLink(true)}
              data-testid="cognara-auto-link-macro-btn"
              style={{ color: '#7C3AED', borderColor: 'rgba(124,58,237,0.4)' }}>
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Cognara: Auto-Link Macro
            </Button>
            <span className="text-xs px-3 py-1.5 rounded-lg" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}
              data-testid="project-label">Project: {selectedProject?.name || 'Select a project'}</span>
          </div>
        </div>

        {/* Tabs — flex-wrap so every button fits without a horizontal scrollbar */}
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1 pb-1 border-b" style={{ borderColor: 'var(--border-color)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-t-lg whitespace-nowrap transition-colors"
                style={{
                  color: activeTab === tab.id ? 'var(--accent-primary)' : 'var(--text-muted)',
                  background: activeTab === tab.id ? 'var(--card-bg)' : 'transparent',
                  borderBottom: activeTab === tab.id ? '2px solid var(--accent-primary)' : '2px solid transparent'
                }}
                data-testid={`tab-${tab.id}`}>
                <Icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab: MCI Register */}
        {activeTab === 'register' && (
          <div className="space-y-3">
            {/* Universal Filter Bar */}
            <UniversalFilterBar
              search={search} onSearchChange={setSearch} searchPlaceholder="Search MCI…"
              category={catFilter} onCategoryChange={setCatFilter}
              categoryOptions={CATEGORY_FILTERS.map(v => ({ value: v, label: v }))}
              phase={phaseFilter} onPhaseChange={setPhaseFilter}
              phaseOptions={PHASE_FILTERS.map(v => ({ value: v, label: v === 'All' ? 'All' : v.replace('_', '-') }))}
              totalCount={totalEntries} totalLabel="items"
              right={
                <div className="flex items-center gap-2">
                  <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }} data-testid="mci-view-mode-toggle">
                    <button onClick={() => setViewMode('register')} data-testid="mci-view-register-btn"
                      className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold transition-colors"
                      style={{
                        backgroundColor: viewMode === 'register' ? '#1E4E8C' : 'var(--card-bg)',
                        color: viewMode === 'register' ? '#ffffff' : 'var(--text-muted)',
                      }}>
                      <List className="w-3.5 h-3.5" /> Register
                    </button>
                    <button onClick={() => setViewMode('matrix')} data-testid="mci-view-matrix-btn"
                      className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold transition-colors"
                      style={{
                        backgroundColor: viewMode === 'matrix' ? '#1E4E8C' : 'var(--card-bg)',
                        color: viewMode === 'matrix' ? '#ffffff' : 'var(--text-muted)',
                        borderLeft: '1px solid var(--border-color)',
                      }}>
                      <Grid3X3 className="w-3.5 h-3.5" /> Matrix
                    </button>
                  </div>
                  <Button size="sm" variant="ghost" onClick={fetchEntries} data-testid="refresh-mci-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" onClick={runRegisterCognara} disabled={bulkBusy}
                    className="h-8 text-xs text-white font-semibold" style={{ background: '#F26B1F' }}
                    data-testid="cognara-forecast-all-mcis-btn"
                    title="Cognara suggests forecast dates for every non-DONE gate across the current filter — human confirms each edit">
                    {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                      <><Sparkles className="w-3.5 h-3.5 mr-1" />Cognara Forecast All MCIs</>}
                  </Button>
                  <PermissionGate module="MCI" action="import">
                    <Button size="sm" variant="outline" onClick={() => setShowExcel(true)} data-testid="excel-btn"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel</Button>
                  </PermissionGate>
                  <PermissionGate module="MCI" action="create">
                    <Button size="sm" onClick={() => setShowAddEntry(true)} data-testid="add-entry-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Entry</Button>
                  </PermissionGate>
                </div>
              }
            />

            {viewMode === 'register' ? (
              <MCIRegisterList entries={entries} loading={loading} search={search} onSearchChange={setSearch} projectId={activeProjectId} onRefresh={fetchEntries} />
            ) : (
              <MCIMatrixGrid entries={entries} projectId={activeProjectId} onRefresh={fetchEntries} isDark={isDark} />
            )}
          </div>
        )}

        {/* Tab: AI Insights */}
        {activeTab === 'ct-pre'  && activeProjectId && <ControlTowerPreOrder projectId={activeProjectId} />}
        {activeTab === 'ct-post' && activeProjectId && <ControlTowerPostOrder projectId={activeProjectId} />}
        {activeTab === 'delivery' && activeProjectId && <DeliveryForecast projectId={activeProjectId} />}
        {activeTab === 'vendors' && activeProjectId && <VendorContactBook projectId={activeProjectId} />}
        {activeTab === 'po' && activeProjectId && <POPerformance projectId={activeProjectId} />}

        {/* Tab: Stage Gate Config */}
        {activeTab === 'config' && <MCIStageGateConfig projectId={activeProjectId} />}

        {/* Tab: Material Types */}
        {activeTab === 'types' && <MaterialTypesConfig projectId={activeProjectId} />}

        {/* Tab: S-Curve & Analytics */}
        {activeTab === 'analytics' && <MCIAnalytics projectId={activeProjectId} />}
        {activeTab === 'proc-analytics' && <ProcurementAnalytics projectId={activeProjectId} />}

        {/* Tab: Long Lead Tracker */}
        {activeTab === 'longlead' && <MCILongLeadTracker projectId={activeProjectId} />}

        {/* Dialogs */}
        <GuidedImportWizard
          open={showExcel}
          onOpenChange={setShowExcel}
          projectId={activeProjectId}
          onImportComplete={fetchEntries}
          {...PROCUREMENT_MCI_CONFIG(activeProjectId)}
        />
        {showAddEntry && <AddEntryDialog projectId={activeProjectId} onClose={() => setShowAddEntry(false)} onCreated={fetchEntries} />}
        {showAutoLink && <CognaraMacroLinkModal projectId={activeProjectId}
          endpointPrefix="/proc" moduleLabel="Procurement"
          onClose={() => setShowAutoLink(false)}
          onCommitted={() => { setShowAutoLink(false); fetchEntries(); }} />}
        {bulkOpen && (
          <BulkForecastReview projectId={activeProjectId} rows={bulkRows}
            title={`Cognara Forecast Register · ${catFilter}`}
            onClose={() => setBulkOpen(false)} onApplied={() => fetchEntries()} />
        )}
      </div>
    </DashboardLayout>
  );
}
